import React, { forwardRef } from 'react';
import { cn } from '@/utils/helpers';

// 按钮变体类型
export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost' | 'outline';

// 按钮尺寸类型
export type ButtonSize = 'sm' | 'md' | 'lg';

// 按钮属性接口
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

// 变体样式映射 - Fluent Design 风格
const variantStyles: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary', 
  success: 'btn-success',
  warning: 'btn-warning',
  error: 'btn-error',
  ghost: 'btn-ghost',
  outline: 'btn-secondary border-2'
};

// 尺寸样式映射 - Fluent Design 风格
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm min-h-[32px]',
  md: 'px-6 py-3 text-sm min-h-[40px]', 
  lg: 'px-8 py-4 text-base min-h-[48px]'
};

// 加载动画组件
const LoadingSpinner: React.FC<{ size: ButtonSize }> = ({ size }) => {
  const spinnerSize = size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <svg
      className={cn('animate-spin', spinnerSize)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // 基础样式
          'inline-flex items-center justify-center font-semibold transition-all duration-300',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'transform hover:scale-105 active:scale-95',
          'relative overflow-hidden',
          // 变体样式
          variantStyles[variant],
          // 尺寸样式
          sizeStyles[size],
          // 全宽样式
          fullWidth && 'w-full',
          // 移动端优化
          'sm:hover:scale-105 active:scale-95',
          // 自定义类名
          className
        )}
        {...props}
      >
        {/* 左侧图标或加载动画 */}
        {loading ? (
          <LoadingSpinner size={size} />
        ) : leftIcon ? (
          <span className={cn('flex-shrink-0', children && 'mr-2')}>
            {leftIcon}
          </span>
        ) : null}

        {/* 按钮文本 */}
        {children && (
          <span className={cn(loading && 'ml-2')}>
            {children}
          </span>
        )}

        {/* 右侧图标 */}
        {rightIcon && !loading && (
          <span className={cn('flex-shrink-0', children && 'ml-2')}>
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;